import React, { ReactElement, useEffect, useState, FormEvent } from 'react';
import { Button, Dropdown, Form, FormControl, InputGroup } from 'react-bootstrap';
import { BiSearch } from 'react-icons/bi';
import { BaseEntite, Produit } from '../../../../../common/src/entities';
import {
    RequeteRechercheApi,
    ReponseRechercheApi,
    RECHERCHE_API,
    RECHERCHE_PAGE_URL,
    reponseVide,
} from '../../../../../common/src/interfaces/rechercheApi';
import api from '../../../Utils/api';
import { useNavigate } from 'react-router-dom';
import './BarreRecherche.scss';
import { State } from 'history';

const listeTypes = [
    'activites',
    'fournisseurs',
    'contacts',
    'clients',
    'lieux',
    'centres-service-scolaire',
    'ententes',
    'produits',
    'modeles-horaire',
] as const;
type BaseEntiteType = typeof listeTypes[number];

const NB_RESULTATS = 5 as const;
const NB_CHAR_MIN_REQ = 3 as const;
const ID_BARRE_RECHERCHE = 'barre-recherche' as const;

/**
 * Barre de recherche dans le dashboard permettant à l'admin de rechercher parmi
 * toutes les entités à la fois.
 * @returns une barre de recherche
 */
function BarreRecherche(): ReactElement {
    const navigate = useNavigate();
    const [resultatsRecherche, setResultatsRecherche] = useState<ReponseRechercheApi>(reponseVide);
    const [valeurCherchee, setValeurCherchee] = useState<string>('');
    const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);

    /**
     * Reporte tout ce qui a été cliqué à l'écran.
     */
    useEffect(() => {
        window.addEventListener('click', gererEventClick);
        return () => window.removeEventListener('click', gererEventClick);
    }, []);

    /**
     * Permet de refermer le dropdown si on clique à l'extérieur du dropdown mais pas si
     * on reclique dans le champ de la barre de recherche. C'est une solution au Dropdown.Toggle
     * (dans react bootstrap) qui toggle le dropdown même quand on reclique dans le champ.
     * @param event ce que la souris a cliqué
     */
    function gererEventClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        setDropdownVisible(target.id === ID_BARRE_RECHERCHE);
    }

    /**
     * Après chaque lettre entrée par l'usager, faire une requete au serveur
     * avec l'api de recherche
     */
    useEffect(() => {
        if (valeurCherchee.length < NB_CHAR_MIN_REQ) {
            setResultatsRecherche(reponseVide);
            return;
        }

        const requete: RequeteRechercheApi = {
            requete: valeurCherchee.trim(),
            nombre: NB_RESULTATS,
        };

        api.get(RECHERCHE_API, { params: requete })
            .then((res) => res.data)
            .then((data) => setResultatsRecherche(data))
            .catch((err) => console.error(err));
    }, [valeurCherchee]);

    /**
     * Callback quand on appuie sur la loupe ou enter depuis la barre de recherche
     * preventDefault empêche d'ouvrir une nouvelle page.
     * @param e event triggered par le enter
     */
    function gererOnSubmit(e: FormEvent): void {
        e.preventDefault();

        if (valeurCherchee.length > 0) {
            // requete provient de RequeteRechercheApi
            navigate(`${RECHERCHE_PAGE_URL}?requete=${valeurCherchee.trim()}`);
            setDropdownVisible(false);
        }
    }

    /**
     * Callback lorsqu'on a cliqué sur un élément du dropdown. Ici on veut le refermer
     * puisqu'une selection a été choisie.
     */
    function gererOnSelect(): void {
        setDropdownVisible(false);
    }

    return (
        <Dropdown show={dropdownVisible}>
            <Form onSubmit={gererOnSubmit}>
                <InputGroup>
                    <FormControl
                        className="shadow-none"
                        type="text"
                        placeholder="Rechercher"
                        onChange={(e) => setValeurCherchee(e.target.value)}
                        value={valeurCherchee}
                        id={ID_BARRE_RECHERCHE}
                    />
                    <InputGroup.Append>
                        <Button type="submit" className="d-flex align-items-center rounded-right">
                            <BiSearch size="1.25em" />
                        </Button>
                    </InputGroup.Append>
                    <Dropdown.Menu className="recherche-dropdown shadow mt-1">
                        <Menu resultatsRecherche={resultatsRecherche} onSelect={gererOnSelect} />
                    </Dropdown.Menu>
                </InputGroup>
            </Form>
        </Dropdown>
    );
}

interface PropsElements {
    entites: BaseEntite[];
    type: BaseEntiteType;
    gererOnClick: (entite: BaseEntite, type: BaseEntiteType) => void;
}

/**
 * Affiche le nom des entités trouvés selon les résultats de la recherche délimité
 * par un titre indiquand le type d'entité
 * @param props de Element
 * @returns chaque élément de la liste
 */
function Elements(props: PropsElements): ReactElement {
    const { entites, type, gererOnClick } = props;

    /**
     * Callback d'une fonction map(). Pour chaque entite trouvé, afficher le nom et associer un
     * évenement clic.
     */
    function creerDropdownItem(entite: BaseEntite, index: number): ReactElement {
        return (
            <Dropdown.Item key={index} eventKey={`${index}`} onClick={() => gererOnClick(entite, type)}>
                {type == 'produits' ? (entite as Produit).fournisseur.name + ' - ' + entite.name : entite.name}
            </Dropdown.Item>
        );
    }

    if (!entites || entites.length < 1) return <></>;

    return (
        <>
            <Dropdown.Header className="text-uppercase py-0">
                <small>{type}</small>
            </Dropdown.Header>
            {entites.map(creerDropdownItem)}
        </>
    );
}

interface PropsMenu {
    resultatsRecherche: ReponseRechercheApi;
    onSelect: () => any;
}

/**
 * Retourne une liste d'éléments selon les résultats de la recherche dans un dropdown
 * en dessous de la barre de recherche.
 * @param props de menu
 * @returns un dropdown
 */
function Menu(props: PropsMenu): ReactElement {
    const { resultatsRecherche, onSelect } = props;
    const navigate = useNavigate();

    /**
     * Associer à chaque résultat un lien vers son formulaire. Envoie aussi un state
     * contenant la liste des autres liens pour la navigation de BoutonPreSuiv
     * @param entite trouvé par la recheche
     * @param type le nom en string de l'entité
     */
    function gererOnClick(entite: BaseEntite, type: BaseEntiteType): void {
        const liens = [
            ...resultatsRecherche.fournisseurs.map((el) => `/dashboard/fournisseurs/${el.id}`),
            ...resultatsRecherche.activites.map((el) => `/dashboard/activites/${el.id}`),
            ...resultatsRecherche.clients.map((el) => `/dashboard/clients/${el.id}`),
            ...resultatsRecherche.contacts.map((el) => `/dashboard/contacts/${el.id}`),
            ...resultatsRecherche.lieux.map((el) => `/dashboard/lieux/${el.id}`),
            ...resultatsRecherche.centresSS.map((el) => `/dashboard/centres-service-scolaire/${el.id}`),
            ...resultatsRecherche.produits.map((el) => `/dashboard/produits/${el.id}`),
        ];

        const position = liens.indexOf(`/dashboard/${type}/${entite.id}`);

        const state: State = {
            pos: position,
            tableauEntite: liens,
        };

        navigate(`${type}/${entite.id}`, { state: state });
        onSelect();
    }

    /**
     * Callback d'une fonction map(). Pour chaque type d'entité, crée une
     * section dans le dropdown. Chaque section contient un titre et les résultats de
     * la recherche.
     * @param type le nom en string de l'entité
     * @returns un résultat clickable dans le dropdown
     */
    function creerElements(type: BaseEntiteType): ReactElement {
        const entites = distinguerResultats(resultatsRecherche, type);
        return <Elements entites={entites} type={type} gererOnClick={gererOnClick} />;
    }

    return <>{listeTypes.map(creerElements)}</>;
}

/**
 * À partir de l'objet réponse retourné par l'api, prendre seulement les résultats
 * d'un entité particulière.
 * @param resultats tous les résultats de la recherche
 * @param type nom en string de l'entité
 * @returns seulement les résultats correspondants à l'entité
 */
function distinguerResultats(resultats: ReponseRechercheApi, type: BaseEntiteType): BaseEntite[] {
    switch (type) {
        case 'activites':
            return resultats.activites;
        case 'fournisseurs':
            return resultats.fournisseurs;
        case 'contacts':
            return resultats.contacts;
        case 'clients':
            return resultats.clients;
        case 'lieux':
            return resultats.lieux;
        case 'centres-service-scolaire':
            return resultats.centresSS;
        case 'ententes':
            return resultats.ententes;
        case 'produits':
            return resultats.produits;
        case 'modeles-horaire':
            return resultats.modelesHoraire;
    }
}

export default BarreRecherche;
